import { jsPDF } from 'jspdf';
import moment from 'moment';
import { money } from '../expenses/expenses.summary';
import { needsReconciliation } from './sales.summary';

/**
 * The sales summary, on paper.
 *
 * The phone's `generateOrderSummaryReport` brought across: the same four stat
 * tiles, the same `# · Product · Quantity · Sales` table, the same closing
 * TOTAL row. Drawn by hand with jsPDF rather than a table plugin, the way the
 * orders export already does it, so no dependency is added.
 *
 * The rows arrive in whatever order the screen is showing, so the sheet and
 * the screen always agree — including the numbering.
 */

const PAGE = { width: 595.28, height: 841.89 }; // A4 portrait, points
const MARGIN = 40;
const LINE = 16;

const COLUMNS = [
  { key: 'rank', label: '#', x: MARGIN, align: 'left', width: 24 },
  { key: 'name', label: 'Product', x: MARGIN + 30, align: 'left', width: 250 },
  { key: 'quantity', label: 'Quantity', x: PAGE.width - MARGIN - 150, align: 'right', width: 80 },
  { key: 'share', label: 'Share', x: PAGE.width - MARGIN - 80, align: 'right', width: 60 },
  { key: 'sales', label: 'Sales', x: PAGE.width - MARGIN, align: 'right', width: 80 },
];

const fmtQuantity = (value) =>
  Number.isInteger(Number(value)) ? String(Number(value)) : Number(value).toFixed(2);

function drawHeaderRow(doc, y) {
  doc.setFontSize(8).setFont('helvetica', 'bold').setTextColor(120);
  COLUMNS.forEach((col) => {
    doc.text(col.label.toUpperCase(), col.x, y, { align: col.align });
  });
  doc.setDrawColor(210).line(MARGIN, y + 5, PAGE.width - MARGIN, y + 5);
  return y + LINE;
}

/**
 * @param {Object} input
 * @param {Array}  input.rows      the rows exactly as the screen lists them
 * @param {Object} input.summary   the totals from summariseSales
 * @param {Object} input.business  the signed-in user's business
 * @param {Object} input.range     {start, end} as YYYY-MM-DD
 * @param {string} input.sortLabel which measure the table is ordered by
 * @returns {string} the filename it saved
 */
export default function exportSalesPdf({ rows, summary, business, range, sortLabel }) {
  const doc = new jsPDF({ unit: 'pt', format: 'a4' });
  const currency = business?.currencyMark || '$';

  let y = MARGIN;

  /* ── who, what, when ─────────────────────────────────────────────────── */

  doc.setFontSize(15).setFont('helvetica', 'bold').setTextColor(20);
  doc.text(business?.businessName || 'Dalabtire', MARGIN, y);
  y += 18;

  doc.setFontSize(10).setFont('helvetica', 'normal').setTextColor(90);
  doc.text('Sales summary', MARGIN, y);

  const rangeLabel =
    range.start === range.end
      ? moment(range.start).format('D MMM YYYY')
      : `${moment(range.start).format('D MMM YYYY')} – ${moment(range.end).format('D MMM YYYY')}`;
  doc.text(rangeLabel, PAGE.width - MARGIN, y, { align: 'right' });
  y += 14;

  doc.setFontSize(8).setTextColor(130);
  doc.text(
    `Sorted by ${sortLabel} · ${summary.orderCount} order${summary.orderCount === 1 ? '' : 's'}`,
    MARGIN,
    y
  );
  doc.text(`Generated ${moment().format('D MMM YYYY HH:mm')}`, PAGE.width - MARGIN, y, {
    align: 'right',
  });
  y += 18;

  /* ── the four figures ────────────────────────────────────────────────── */

  doc.setDrawColor(225).setFillColor(247, 247, 249);
  doc.rect(MARGIN, y, PAGE.width - MARGIN * 2, 44, 'F');

  const slots = [
    ['Products', String(summary.productCount)],
    ['Items sold', fmtQuantity(summary.unitsSold)],
    ['Total sales', money(summary.sales, currency)],
    // Negated rather than prefixed: money() prints its own minus, and a
    // prefix on top of it reads "−−$5.00".
    ['Discount', money(-summary.discount, currency)],
  ];
  const slotWidth = (PAGE.width - MARGIN * 2) / slots.length;

  slots.forEach(([label, value], index) => {
    const x = MARGIN + slotWidth * index + 10;
    doc.setFontSize(7.5).setFont('helvetica', 'normal').setTextColor(130);
    doc.text(label.toUpperCase(), x, y + 16);
    doc.setFontSize(11).setFont('helvetica', 'bold');
    // The discount is the one figure that is money going the other way.
    if (label === 'Discount' && summary.discount > 0) doc.setTextColor(200, 45, 40);
    else doc.setTextColor(20);
    doc.text(value, x, y + 32);
  });
  y += 60;

  /* ── the breakdown ───────────────────────────────────────────────────── */

  doc.setFontSize(9).setFont('helvetica', 'bold').setTextColor(35);
  doc.text('Product breakdown', MARGIN, y);
  y += 14;

  y = drawHeaderRow(doc, y);

  const whole = summary.sales || 0;

  rows.forEach((row, index) => {
    if (y > PAGE.height - MARGIN - LINE * 2) {
      doc.addPage();
      y = MARGIN;
      y = drawHeaderRow(doc, y);
    }

    const cells = {
      rank: String(index + 1),
      name: row.name,
      quantity: fmtQuantity(row.quantity),
      share: whole ? `${((row.sales / whole) * 100).toFixed(1)}%` : '—',
      sales: money(row.sales, currency),
    };

    doc.setFontSize(9).setFont('helvetica', 'normal').setTextColor(35);
    COLUMNS.forEach((col) => {
      doc.setFont('helvetica', col.key === 'sales' ? 'bold' : 'normal');
      doc.setTextColor(col.key === 'rank' || col.key === 'share' ? 130 : 35);
      // Truncated rather than allowed to run into the next column.
      const text = doc.splitTextToSize(String(cells[col.key] || ''), col.width)[0];
      doc.text(text, col.x, y, { align: col.align });
    });

    doc.setDrawColor(238).line(MARGIN, y + 4, PAGE.width - MARGIN, y + 4);
    y += LINE;
  });

  /* ── what it comes to ────────────────────────────────────────────────── */

  if (y > PAGE.height - MARGIN - LINE * 3) {
    doc.addPage();
    y = MARGIN;
  }

  y += 6;
  doc.setFontSize(10).setFont('helvetica', 'bold').setTextColor(20);
  doc.text('TOTAL', MARGIN, y);
  doc.text(money(summary.sales, currency), PAGE.width - MARGIN, y, { align: 'right' });
  y += LINE;

  /**
   * The item lines and the takings only differ when a discount was given, and
   * when they do the sheet says so rather than leaving somebody to find the
   * gap with a calculator.
   */
  if (needsReconciliation(summary)) {
    doc.setFontSize(9).setFont('helvetica', 'normal').setTextColor(120);
    // A positive gap is a discount. A negative one should not happen at all —
    // it would mean the shop took more than it sold — so it is named for what
    // it is rather than mislabelled as a discount.
    doc.text(summary.gap >= 0 ? 'Less discount' : 'Adjustment', MARGIN, y);
    doc.setTextColor(200, 45, 40);
    doc.text(money(-summary.gap, currency), PAGE.width - MARGIN, y, { align: 'right' });
    y += LINE;

    doc.setFontSize(10).setFont('helvetica', 'bold').setTextColor(20);
    doc.text('NET TAKINGS', MARGIN, y);
    doc.text(money(summary.net, currency), PAGE.width - MARGIN, y, { align: 'right' });
  }

  const filename = `sales-summary-${range.start}${
    range.start === range.end ? '' : `_${range.end}`
  }.pdf`;
  doc.save(filename);
  return filename;
}
